import { useEffect, useRef, useState } from "react";
import { useTourStoreApi } from "@heroncendre/player";
import { COULEURS } from "./splashStyles.js";
import { useReducedMotion } from "./useReducedMotion.js";

/**
 * ============================================================================
 *  FonduEntree.tsx — Le noir qui enchaîne l'accueil et la première scène du guide
 * ----------------------------------------------------------------------------
 *  Choisir un parcours guidé à l'accueil passe l'écran au NOIR (`SplashScreen`,
 *  `voile`) ; l'accueil disparaît ensuite, et le guide démarre sur sa première scène.
 *  Sans ce composant, le visiteur verrait entre les deux la scène de fond — celle de
 *  l'accueil — se couper sec sur la première du guide, avant que les bandes n'entrent.
 *
 *  Il tient donc le noir, de la même teinte que celui de l'accueil, par-dessus tout, et le
 *  lève quand le guide a démarré ET que sa première scène est à l'écran :
 *
 *   - la scène du guide n'est pas celle de l'accueil : on attend son APPARITION
 *     (`sceneReveals`), pas avant — même règle que le fondu entre chapitres
 *     (`useGuide`) ;
 *   - c'est la même : rien n'apparaît, on ne l'attend pas (400 ms).
 *
 *  Deux sécurités plafonnent l'attente, pour qu'un panorama qui ne charge pas ne laisse
 *  jamais l'écran noir. Mouvement réduit : un fondu bref.
 * ==========================================================================*/

/** Plafond de l'attente d'une scène qui n'apparaît pas. */
const ATTENTE_MAX_MS = 5000;
/** Plafond absolu : le guide n'a jamais démarré. */
const SECURITE_MS = 10000;
/** Même scène en tête du guide : rien n'apparaît, un souffle suffit. */
const MEME_SCENE_MS = 400;
const FONDU_MS = 800;
const FONDU_REDUIT_MS = 200;

export function FonduEntree({ surFin, suspendu = false }: { surFin: () => void; /** Le visiteur doit répondre (`ChoixReprise`) : le plafond de l’attente est suspendu. */ suspendu?: boolean }) {
  const store = useTourStoreApi();
  const reduit = useReducedMotion();
  const [leve, setLeve] = useState(false);
  const fin = useRef(surFin);
  fin.current = surFin;
  const duree = reduit ? FONDU_REDUIT_MS : FONDU_MS;
  const lever = useRef<(() => void) | null>(null);

  useEffect(() => {
    const avant = store.getState().currentSceneId;
    let demarre = false;
    let base = 0;
    let fini = false;
    const minuteurs: number[] = [];

    const leverLa = () => {
      if (fini) return;
      fini = true;
      arreter();
      setLeve(true);
      minuteurs.push(window.setTimeout(() => fin.current(), duree + 50));
    };

    const surEtat = () => {
      const etat = store.getState();
      if (!demarre) {
        if (etat.guided.state !== "narration") return;
        demarre = true;
        base = etat.sceneReveals;
        const memeScene = etat.currentSceneId === avant;
        minuteurs.push(window.setTimeout(leverLa, memeScene ? MEME_SCENE_MS : ATTENTE_MAX_MS));
        return;
      }
      if (etat.sceneReveals > base) leverLa();
    };

    const arreter = store.subscribe(surEtat);
    lever.current = leverLa;
    surEtat();
    return () => {
      fini = true;
      arreter();
      for (const minuteur of minuteurs) window.clearTimeout(minuteur);
    };
  }, [store, duree]);

  // Plafond absolu : le guide n’a jamais démarré. Suspendu pendant que le visiteur répond à la question.
  useEffect(() => {
    if (suspendu) return;
    const minuteur = window.setTimeout(() => lever.current?.(), SECURITE_MS);
    return () => window.clearTimeout(minuteur);
  }, [suspendu]);

  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 50,
        background: COULEURS.fond,
        opacity: leve ? 0 : 1,
        // Opaque d'emblée : l'accueil est déjà noir ; seul le retour de l'image s'anime.
        transition: leve ? `opacity ${duree}ms ease` : "none",
        // Noir tenu : aucun geste ne doit atteindre la scène ou le guide dessous.
        pointerEvents: leve ? "none" : "auto",
      }}
    />
  );
}
